import React, { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { AlertTriangle, Shield, X, Eye } from 'lucide-react';
import { toast } from 'sonner';

interface SecurityAlert {
  id: string;
  event_type: string;
  details: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  created_at: string;
  acknowledged?: boolean;
}

export function SecurityAlertSystem() {
  const { isAdmin, user } = useAuth();
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [showAlerts, setShowAlerts] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;

    // Fetch recent critical/high alerts
    fetchRecentAlerts();

    // Set up real-time subscription for new alerts
    const subscription = supabase
      .channel('security_alerts')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'security_events',
          filter: `severity.in.(critical,high)`
        },
        (payload) => {
          const newAlert = payload.new as SecurityAlert;
          setAlerts(prev => [newAlert, ...prev.slice(0, 4)]); // Keep only 5 most recent
          
          // Show toast notification for critical alerts
          if (newAlert.severity === 'critical') {
            toast.error(`Critical Security Alert: ${newAlert.event_type}`, {
              description: newAlert.details,
              duration: 10000,
              action: {
                label: 'View Details',
                onClick: () => setShowAlerts(true)
              }
            });
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [isAdmin]);

  const fetchRecentAlerts = async () => {
    try {
      const { data, error } = await supabase
        .from('security_events')
        .select('*')
        .in('severity', ['critical', 'high'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) throw error;
      setAlerts((data || []).map(item => ({
        ...item,
        severity: item.severity as 'critical' | 'high' | 'medium' | 'low'
      })));
    } catch (error) {
      console.error('Failed to fetch security alerts:', error);
    }
  };

  const acknowledgeAlert = async (alertId: string) => {
    setAlerts(prev => prev.filter(alert => alert.id !== alertId));
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'default';
      default: return 'secondary';
    }
  };

  const getSeverityIcon = (severity: string) => {
    return severity === 'critical' || severity === 'high' 
      ? <AlertTriangle className="h-4 w-4" />
      : <Shield className="h-4 w-4" />;
  };

  if (!isAdmin || alerts.length === 0) return null;

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const highCount = alerts.filter(a => a.severity === 'high').length;

  return (
    <div className="fixed top-4 right-4 z-50 max-w-md">
      {/* Alert Toggle Button */}
      {!showAlerts && (criticalCount > 0 || highCount > 0) && (
        <Button
          onClick={() => setShowAlerts(true)}
          variant={criticalCount > 0 ? "destructive" : "outline"}
          size="sm"
          className="mb-2 animate-pulse"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          {criticalCount > 0 ? `${criticalCount} Critical` : `${highCount} High`} Alert{(criticalCount + highCount) > 1 ? 's' : ''}
        </Button>
      )}

      {/* Alert Panel */}
      {showAlerts && (
        <div className="space-y-2 bg-background border rounded-lg shadow-lg p-4 max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-red-500" />
              <span className="font-semibold">Security Alerts</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowAlerts(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {alerts.map((alert) => (
            <Alert key={alert.id} className="relative">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-2 flex-1">
                  {getSeverityIcon(alert.severity)}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-sm">
                        {alert.event_type.replace('_', ' ').toUpperCase()}
                      </span>
                      <Badge variant={getSeverityColor(alert.severity)} className="text-xs">
                        {alert.severity}
                      </Badge>
                    </div>
                    <AlertDescription className="text-xs">
                      {alert.details}
                    </AlertDescription>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(alert.created_at).toLocaleString()}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => acknowledgeAlert(alert.id)}
                  className="ml-2 h-6 w-6 p-0"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </Alert>
          ))}

          {alerts.length === 0 && (
            <p className="text-center text-sm text-muted-foreground py-4">
              No recent security alerts
            </p>
          )}

          <div className="flex justify-center pt-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowAlerts(false);
                // Navigate to security monitor tab if available
                const url = new URL(window.location.href);
                url.pathname = '/dashboard';
                url.searchParams.set('tab', 'security');
                window.location.href = url.toString();
              }}
            >
              <Eye className="h-4 w-4 mr-2" />
              View All Security Events
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}